import UploadInput from "./UploadInput";

export interface SettingValues {
  [key: string]: string;
}

interface GeneralTabProps {
  values: SettingValues;
  onUpdate: (key: string, value: string) => void;
}

interface FieldGroup {
  title: string;
  icon: string;
  blurb: string;
  fields: {
    key: string;
    label: string;
    type?: "text" | "textarea" | "upload" | "brochure";
    placeholder?: string;
    hint?: string;
  }[];
}

const GROUPS: FieldGroup[] = [
  {
    title: "Branding",
    icon: "ri-palette-line",
    blurb: "Site name, logo, favicon and tagline shown across the whole website.",
    fields: [
      { key: "site_name", label: "Site name", placeholder: "NOMADLIVINGS" },
      { key: "site_tagline", label: "Tagline", placeholder: "Luxury A-Frame Villa Investment" },
      { key: "site_url", label: "Site URL", placeholder: "https://yoursite.com", hint: "Used for sitemap and canonical links." },
      { key: "brand_logo_url", label: "Logo image", type: "upload", hint: "Shown in the header across the site." },
      { key: "brand_favicon_url", label: "Favicon", type: "upload", hint: "Small icon shown in the browser tab." },
    ],
  },
  {
    title: "Default SEO",
    icon: "ri-search-line",
    blurb: "Site-wide fallback meta tags when a page has no custom SEO set.",
    fields: [
      { key: "seo_title", label: "Default meta title", hint: "Used when a page has no title (aim for ≤ 60 chars)." },
      { key: "seo_description", label: "Default meta description", type: "textarea", hint: "Aim for 120–160 characters." },
      { key: "seo_keywords", label: "Default keywords", hint: "Comma-separated keywords." },
      { key: "og_image_url", label: "Social share image (OG)", type: "upload", hint: "Shown when the site is shared on social media." },
    ],
  },
  {
    title: "Contact",
    icon: "ri-phone-line",
    blurb: "Public contact details shown on the website footer and contact page.",
    fields: [
      { key: "contact_phone", label: "Phone", placeholder: "80883 30330" },
      { key: "contact_whatsapp", label: "WhatsApp", placeholder: "80883 30330" },
      { key: "contact_email", label: "Email", placeholder: "invest@nomadlivings.com" },
      { key: "contact_address", label: "Address", placeholder: "Prime Tourism Destination, India" },
    ],
  },
  {
    title: "Social",
    icon: "ri-share-line",
    blurb: "Social profile links used in the footer.",
    fields: [
      { key: "social_facebook", label: "Facebook URL", placeholder: "https://facebook.com/..." },
      { key: "social_instagram", label: "Instagram URL", placeholder: "https://instagram.com/..." },
      { key: "social_linkedin", label: "LinkedIn URL", placeholder: "https://linkedin.com/..." },
    ],
  },
  {
    title: "Brochure",
    icon: "ri-file-download-line",
    blurb: "The main company brochure used by the 'Download Brochure' button across the website.",
    fields: [
      { key: "brochure_url", label: "Main site brochure (PDF)", type: "brochure", hint: "Shown to visitors on the homepage download button." },
    ],
  },
];

export default function GeneralTab({ values, onUpdate }: GeneralTabProps) {
  const base = "w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400";

  return (
    <div className="space-y-5">
      {GROUPS.map((group) => (
        <div
          key={group.title}
          className="rounded-2xl bg-background-50 border border-background-200 overflow-hidden"
        >
          <div className="px-5 py-4 border-b border-background-100">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-primary-100 text-primary-700">
                <i className={group.icon}></i>
              </span>
              <div>
                <h3 className="font-heading text-base text-foreground-950">{group.title}</h3>
                <p className="text-xs text-foreground-600">{group.blurb}</p>
              </div>
            </div>
          </div>
          <div className="divide-y divide-background-100">
            {group.fields.map((field) => (
              <div key={field.key} className="flex flex-col sm:flex-row sm:items-start gap-3 px-5 py-4">
                <div className="sm:w-64 shrink-0">
                  <p className="text-sm font-medium text-foreground-950">{field.label}</p>
                  {field.hint && (
                    <p className="text-[11px] text-foreground-600 mt-0.5">{field.hint}</p>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  {field.type === "brochure" || field.type === "upload" ? (
                    <UploadInput
                      value={values[field.key] ?? ""}
                      onChange={(v) => onUpdate(field.key, v)}
                      type={field.type === "brochure" ? "brochure" : "image"}
                      label={field.type === "brochure" ? "Upload PDF" : "Upload"}
                    />
                  ) : field.type === "textarea" ? (
                    <textarea
                      rows={3}
                      value={values[field.key] ?? ""}
                      onChange={(e) => onUpdate(field.key, e.target.value)}
                      placeholder={field.placeholder}
                      className={`${base} resize-none`}
                    />
                  ) : (
                    <input
                      value={values[field.key] ?? ""}
                      onChange={(e) => onUpdate(field.key, e.target.value)}
                      placeholder={field.placeholder}
                      className={base}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}